import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(duration)
dayjs.extend(relativeTime)

export const formatPrice = function (price: number, addPlus = false, priceSymbol = 'Credit') {
  if (isNaN(Number(price))) {
    return price
  }

  if (priceSymbol !== '') {
    priceSymbol = Math.abs(price) > 100 ? 'Credits' : 'Credit'
  }

  return formatMillicentPrice(price * 1000, addPlus, priceSymbol)
}

export function formatMillicentPrice(price: number, addPlus = false, priceSymbol = 'Credit') {
  if (isNaN(Number(price))) {
    return price
  }

  price = Math.round(price) / 100000

  let isMinus = false
  if (price < 0) {
    isMinus = true
  }

  if (Math.abs(price) > 1000000000) {
    return `${isMinus ? '-' : ''}9,999,999+`
  }

  const resultString = Math.abs(price).toFixed(3)
  const resultList = resultString.split('')
  resultList.push(' ' + priceSymbol)

  if (isMinus) {
    resultList.unshift('-')
  } else if (!isMinus && Math.abs(price) && addPlus) {
    resultList.unshift('+')
  }

  return resultList.join('')
}

export function formatDomain(domain: number) {
  return `#${domain + 1}`
}

export function formatRegion(domain: number) {
  return `Region ${domain + 1}`
}

const PRESET_FMT = {
  day: 'YYYY-MM-DD',
  minute: 'YYYY-MM-DD HH:mm',
  second: 'YYYY-MM-DD HH:mm:ss',
} as const
export const formatDatePreset = function (
  date: string | number | Date | dayjs.Dayjs,
  type: keyof typeof PRESET_FMT | string = 'day',
) {
  if (!date) {
    return '-'
  }

  let fmt = type
  if (Object.keys(PRESET_FMT).includes(type)) {
    fmt = PRESET_FMT[type as keyof typeof PRESET_FMT]
  }
  const parsedDate = dayjs(date)
  return parsedDate.isValid() ? parsedDate.format(fmt) : '-'
}

export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 B'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.max(0, Math.floor(Math.log(bytes) / Math.log(k)))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function formatBitsPerSec(bps: number) {
  if (!+bps) return '0bps'
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps']
  const k = 1000
  const i = Math.max(0, Math.floor(Math.log(bps) / Math.log(k)))
  return `${parseFloat((bps / Math.pow(k, i)).toFixed(2))}${units[i]}`
}

/**
 * @deprecated Use toBytes from '@/utils/storage' instead.
 * Example: toBytes(gb, 'gb')
 */
export function formatGB2Bytes(gb: number) {
  return gb * 1024 * 1024 * 1024
}

/**
 * @deprecated Use toBytes from '@/utils/storage' instead.
 * Example: toBytes(mb, 'mb')
 */
export function formatMB2Bytes(mb: number) {
  return mb * 1024 * 1024
}

/**
 * @deprecated Use formatStorageGB from '@/utils/storage' instead.
 * Example: formatStorageGB(bytes, { includeUnit: false })
 */
export function formatBytesToGB(bytes: number, decimals: number = 2): string {
  if (!bytes) return '0'
  const gb = bytes / (1024 * 1024 * 1024)
  return gb.toFixed(decimals)
}

export const formatDuration = function (timeSec: number, detailed = false) {
  if (!detailed) {
    return dayjs.duration(timeSec, 'seconds').humanize()
  }
  const days = Math.floor(timeSec / 86400)
  const hours = Math.floor((timeSec % 86400) / 3600)
  const mins = Math.floor((timeSec % 3600) / 60)
  const parts: string[] = []
  if (days > 0) parts.push(`${days}days`)
  if (hours > 0) parts.push(`${hours}hrs`)
  if (mins > 0 || parts.length === 0) parts.push(`${mins}mins`)
  return parts.join(' ')
}

export const formatDurationToHours = function (timeSec: number) {
  return dayjs.duration(timeSec, 'seconds').asHours().toFixed(1)
}

export const formatRamMB = (ramMB: number) => {
  if (ramMB > 1024) {
    return `${parseInt(ramMB / 1024 + 0.5 + '')} GB`
  } else {
    return `${ramMB} MB`
  }
}

export function formatToDynamicDecimalPlaces(value: number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatBandwidthBit(value: number) {
  if (value >= 1e9) {
    return (value / 1e9).toFixed(1) + ' Gbps'
  } else if (value >= 1e6) {
    return (value / 1e6).toFixed(0) + ' Mbps'
  } else if (value >= 1e3) {
    return (value / 1e3).toFixed(0) + ' Kbps'
  } else {
    return value + ' bps'
  }
}

export function formatCompactNumber(value: number, decimals = 1): string {
  if (isNaN(Number(value))) {
    return `${value}`
  }
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Format a decimal string (e.g. Canvas API Credits like '0.300000000000000000')
 * without converting to float, preserving precision. Trims trailing zeros down
 * to `minFraction` digits and truncates beyond `maxFraction` digits.
 */
export function formatDecimalString(value: string, minFraction = 0, maxFraction = 18): string {
  if (!/^-?\d+(\.\d+)?$/.test(value)) {
    return value
  }

  const [rawInt, rawFrac = ''] = value.split('.')
  let frac = rawFrac.slice(0, maxFraction).replace(/0+$/, '')
  if (frac.length < minFraction) {
    frac = frac.padEnd(minFraction, '0')
  }
  return frac.length > 0 ? `${rawInt}.${frac}` : rawInt
}

/** Milliseconds → seconds for display (2 decimal places), e.g. 842 → 0.84. */
export function formatMsToSeconds(ms: number, decimals = 2): number {
  const factor = Math.pow(10, decimals)
  return Math.round((ms / 1000) * factor) / factor
}

export function maskPassword(password: string): string {
  if (password.length <= 3) {
    return password
  }
  return password.slice(0, 3) + '*'.repeat(password.length - 3)
}

export function formatFloat(
  value: number,
  precision: number = 2,
  useGrouping: boolean = false,
): string {
  if (useGrouping) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
  }
  return value.toFixed(precision)
}

export function formatSpeed(bytesPerSecond: number): string {
  if (!bytesPerSecond || bytesPerSecond <= 0) return ''

  const k = 1024
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s']

  const i = Math.max(0, Math.floor(Math.log(bytesPerSecond) / Math.log(k)))
  const value = bytesPerSecond / Math.pow(k, i)

  return `${value.toFixed(1)} ${sizes[i]}`
}
